import type { BreakdownRow, BreakdownSortKey } from "./breakdown-sort";
import {
  clickThroughRate,
  costPerClick,
  postClickConversionRate,
  ratePerMille,
} from "./breakdown-sort";
import type { ExtraMetric } from "./extra-metrics";
import { formatMetricValue } from "./grid-format";

// The configurable metric vocabulary shared by By campaign (and, per issue 13,
// By creative). Identity columns — name, platform, and the creative format —
// always render and are not part of this set; everything here can be shown or
// hidden and the choice is remembered per user (see load/saveBreakdownLayout).
export type BreakdownMetricKey =
  | "delivery"
  | "spend"
  | "impressions"
  | "cpm"
  | "clicks"
  | "ctr"
  | "conversions"
  | "costPerConversion"
  | "cpc"
  | "cvr"
  | ExtraColumnKey;

/** Columns a Company's extra metrics add; each shows only when the Company opted in. */
export type ExtraColumnKey =
  | "reach"
  | "frequency"
  | "thruplays"
  | "landingPageViews"
  | "linkClicks"
  | "engagement";

export type BreakdownKind = "campaign" | "creative";

export interface BreakdownColumn {
  key: BreakdownMetricKey;
  label: string;
  // "status" is the Delivery pill; "num" is a right-aligned numeric cell.
  kind: "status" | "num";
  // Conversions and cost per conversion each expand into their Mapped parts.
  expandable?: boolean;
  sortKey: BreakdownSortKey;
  // Numeric value for the cell and for sorting; null where a ratio's denominator
  // is zero. Not used for the Delivery column (kind === "status").
  value?: (row: BreakdownRow) => number | null;
  format?: (value: number | null, currency: string) => string;
  /** The extra metric a Company opts into for this column to be offered. */
  extra?: ExtraMetric;
}

const DASH = "—";

// CTR and post-click conversion rate are percentages; the shared currency
// formatter rounds to whole numbers, which would erase a 1.4% CTR, so rates get
// one decimal here. null (no impressions/clicks) renders as an em dash.
export function formatRate(value: number | null): string {
  if (value == null) return DASH;
  return `${value.toLocaleString("nb-NO", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function formatCurrency(value: number | null, currency: string): string {
  return value == null ? DASH : formatMetricValue(value, "currency", currency);
}

function formatNumber(value: number | null, currency: string): string {
  return value == null ? DASH : formatMetricValue(value, "number", currency);
}

// Canonical, ordered catalog. Column order in the table follows this array so a
// remembered layout never reshuffles columns; only visibility changes.
export const BREAKDOWN_COLUMNS: BreakdownColumn[] = [
  { key: "delivery", label: "Delivery", kind: "status", sortKey: "delivery" },
  {
    key: "spend",
    label: "Spend",
    kind: "num",
    sortKey: "spend",
    value: (row) => row.spend,
    format: formatCurrency,
  },
  {
    key: "impressions",
    label: "Impressions",
    kind: "num",
    sortKey: "impressions",
    value: (row) => row.impressions,
    format: formatNumber,
  },
  {
    key: "cpm",
    label: "CPM",
    kind: "num",
    sortKey: "cpm",
    value: ratePerMille,
    format: formatCurrency,
  },
  {
    key: "clicks",
    label: "Clicks",
    kind: "num",
    sortKey: "clicks",
    value: (row) => row.clicks,
    format: formatNumber,
  },
  {
    key: "ctr",
    label: "CTR",
    kind: "num",
    sortKey: "ctr",
    value: clickThroughRate,
    format: (value) => formatRate(value),
  },
  {
    key: "conversions",
    label: "Conversions",
    kind: "num",
    expandable: true,
    sortKey: "conversions",
    value: (row) => row.conversions,
    format: formatNumber,
  },
  {
    key: "costPerConversion",
    label: "Cost / conversion",
    kind: "num",
    expandable: true,
    sortKey: "costPerConversion",
    value: (row) => row.costPerConversion,
    format: (value, currency) =>
      value == null ? DASH : formatMetricValue(value, "currency_per", currency),
  },
  {
    key: "cpc",
    label: "CPC",
    kind: "num",
    sortKey: "cpc",
    value: costPerClick,
    format: formatCurrency,
  },
  {
    key: "cvr",
    label: "Conversion rate",
    kind: "num",
    sortKey: "cvr",
    value: postClickConversionRate,
    format: (value) => formatRate(value),
  },
  // Extra metrics (Settings › Reporting › Extra metrics). Reach is the platform's
  // count of people for the period and Filter, so it is never added up.
  ...(
    [
      ["reach", "Reach", "reach"],
      ["frequency", "Frequency", "reach"],
      ["thruplays", "ThruPlays", "thruplays"],
      ["landingPageViews", "Landing page views", "landing_page_views"],
      ["linkClicks", "Link clicks", "link_clicks"],
      ["engagement", "Engagement", "engagement"],
    ] as const
  ).map(([key, label, extra]): BreakdownColumn => ({
    key,
    label,
    kind: "num",
    sortKey: key,
    extra,
    value: (row) => row.extra?.[key] ?? null,
    format:
      key === "frequency"
        ? (value) =>
            value == null ? DASH : value.toLocaleString("nb-NO", { maximumFractionDigits: 2 })
        : formatNumber,
  })),
];

/** The columns a Company may show: its extra metrics only when opted into. */
export function availableBreakdownColumns(extraMetrics: readonly ExtraMetric[]) {
  return BREAKDOWN_COLUMNS.filter((column) => !column.extra || extraMetrics.includes(column.extra));
}

// Issue 12 defaults: Delivery, Spend, Impressions, CPM, Clicks, CTR, Conversions,
// and cost per conversion. CPC and post-click conversion rate stay off by default
// so they don't crowd the table, but remain one click away in Display.
export const DEFAULT_BREAKDOWN_METRICS: BreakdownMetricKey[] = [
  "delivery",
  "spend",
  "impressions",
  "cpm",
  "clicks",
  "ctr",
  "conversions",
  "costPerConversion",
];

const KNOWN_METRIC_KEYS = new Set<string>(BREAKDOWN_COLUMNS.map((column) => column.key));

// Coerce a stored/loaded selection back to a valid, catalog-ordered, de-duplicated
// list. Unknown keys (from a newer or corrupt payload) are dropped; an empty or
// invalid selection falls back to the defaults so the table is never blank.
export function resolveBreakdownMetrics(
  selection: readonly string[] | null | undefined,
): BreakdownMetricKey[] {
  if (!selection || selection.length === 0) return [...DEFAULT_BREAKDOWN_METRICS];
  const chosen = new Set(selection.filter((key) => KNOWN_METRIC_KEYS.has(key)));
  if (chosen.size === 0) return [...DEFAULT_BREAKDOWN_METRICS];
  return BREAKDOWN_COLUMNS.filter((column) => chosen.has(column.key)).map((column) => column.key);
}

// ---- Per-user persistence ----------------------------------------------------
// A column layout is a personal choice, not workspace configuration, so it lives
// in the user's own browser (like the creative-thumbnail cache) rather than in a
// shared workspace record. This keeps one user's layout from ever mutating
// another's, needs no schema, and is restored on the next visit.

const BREAKDOWN_LAYOUT_VERSION = 1;

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

function defaultStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

function layoutKey(orgSlug: string, kind: BreakdownKind) {
  return `aco:breakdown-columns:v${BREAKDOWN_LAYOUT_VERSION}:${orgSlug}:${kind}`;
}

export function loadBreakdownLayout(
  orgSlug: string,
  kind: BreakdownKind,
  storage: ReadableStorage | undefined = defaultStorage(),
): BreakdownMetricKey[] | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(layoutKey(orgSlug, kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;
    return resolveBreakdownMetrics(parsed.filter((key): key is string => typeof key === "string"));
  } catch {
    // A corrupt or unavailable store should never block reporting; fall back to
    // defaults by returning null.
    return null;
  }
}

export function saveBreakdownLayout(
  orgSlug: string,
  kind: BreakdownKind,
  metrics: readonly BreakdownMetricKey[],
  storage: WritableStorage | undefined = defaultStorage(),
) {
  if (!storage) return;
  try {
    storage.setItem(layoutKey(orgSlug, kind), JSON.stringify(resolveBreakdownMetrics(metrics)));
  } catch {
    // Storage can be disabled or full; the in-memory selection still applies for
    // this session.
  }
}
